import React, { useState, useMemo } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Table
 * @param {{key: string, header: string, render?: (row: any) => React.ReactNode, sortable?: boolean, nowrap?: boolean}[]} columns
 * @param {any[]} data
 * @param {boolean} [isLoading] - shows skeleton rows
 * @param {React.ReactNode} [emptyState] - shown when data is empty
 * @param {(row: any) => void} [onRowClick]
 * @param {"comfortable"|"compact"} [density="comfortable"] - compact tightens row height for long lists
 * @param {string} [className]
 */
export function Table({
  columns = [],
  data = [],
  isLoading = false,
  emptyState,
  onRowClick,
  density = "comfortable",
  className,
  ...props
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const cellPadding = density === "compact" ? "px-4 py-2" : "px-4 py-3";
  const headerPadding = density === "compact" ? "px-4 py-2" : "px-4 py-3";

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [data, sortKey, sortDir]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  return (
    <div className={cn("overflow-hidden rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-card dark:shadow-black/20", className)} {...props}>
      <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-neutral-50 dark:bg-neutral-950">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(headerPadding, "text-xs font-medium uppercase tracking-wide text-neutral-600 dark:text-neutral-300", col.nowrap && "whitespace-nowrap")}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded"
                  >
                    {col.header}
                    {sortKey === col.key ? (
                      sortDir === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                      )
                    ) : (
                      <ChevronsUpDown className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                    )}
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="border-b border-neutral-200 dark:border-neutral-800">
                {columns.map((col) => (
                  <td key={col.key} className={cellPadding}>
                    <div className="h-4 w-full max-w-[120px] animate-pulse rounded bg-neutral-100 dark:bg-neutral-800" />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading && sortedData.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center">
                {emptyState || <span className="text-sm text-neutral-500 dark:text-neutral-400">No data available</span>}
              </td>
            </tr>
          )}

          {!isLoading &&
            sortedData.map((row, rowIndex) => (
              <tr
                key={row.id ?? rowIndex}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  "border-b border-neutral-200 dark:border-neutral-800 last:border-b-0",
                  onRowClick && "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
                )}
              >
                {columns.map((col) => (
                  <td key={col.key} className={cn(cellPadding, "text-neutral-900 dark:text-neutral-100", col.nowrap && "whitespace-nowrap")}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}